import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { useBrowserSTT } from '@/hooks/useBrowserSTT';
import { useVoiceCommands } from '@/hooks/useVoiceCommands';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import T from '@/components/T';

interface VoiceInputButtonProps {
  className?: string;
}

export const VoiceInputButton = ({ className }: VoiceInputButtonProps) => {
  const { toast } = useToast();
  const { processCommand, isProcessing, stopSpeaking } = useVoiceCommands();
  const [isEnabled, setIsEnabled] = useState(false);
  const processingRef = useRef(false);

  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('voice_settings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        setIsEnabled(settings.enabled ?? false);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  const browserSTT = useBrowserSTT({
    language: 'ru-RU',
    onResult: async (text, isFinal) => {
      if (isFinal && text.trim() && !processingRef.current) {
        processingRef.current = true;
        
        toast({
          title: "🎤 Распознано",
          description: text.trim(),
        });

        // Process voice command through AI
        try {
          await processCommand(text.trim());
        } finally {
          processingRef.current = false;
        }
      }
    },
    onError: (error) => {
      toast({
        title: "Ошибка распознавания",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Don't render if voice is disabled or not supported
  if (!isEnabled || !browserSTT.isSupported) {
    return null;
  }

  const handleClick = () => {
    if (browserSTT.isListening) {
      browserSTT.stop();
    } else {
      stopSpeaking(); // Stop any ongoing TTS
      browserSTT.start();
    }
  };

  const isActive = browserSTT.isListening || isProcessing;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClick}
            disabled={isProcessing}
            className={cn(
              "relative p-2 transition-all",
              isActive && "text-primary",
              browserSTT.isListening && "animate-pulse",
              className
            )}
          >
            {isProcessing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : browserSTT.isListening ? (
              <>
                <MicOff className="h-5 w-5" />
                {/* Pulsing ring when listening */}
                <span className="absolute inset-0 rounded-md bg-primary/20 animate-ping" />
              </>
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isProcessing ? (
            <T ru="Обрабатываю команду..." en="Processing command..." />
          ) : browserSTT.isListening ? (
            <T ru="Нажмите чтобы остановить" en="Click to stop" />
          ) : (
            <T ru="Голосовой ввод (скажите команду)" en="Voice input (say a command)" />
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
